import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const PRIMARY = {
  lighter: '#D0CCFF',
  light: '#8A80FF',
  main: '#573CFE',
  dark: '#291DB6',
  darker: '#1E1665',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  lighter: '#E5FDFF',
  light: '#99F6FF',
  main: '#40DAEA',
  dark: '#0BB1C1',
  darker: '#012D32',
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: '#DCD9FF',
  light: '#8A80FF',
  main: '#573CFE',
  dark: '#291DB6',
  darker: '#0A062D',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#D6FFDD',
  light: '#86F098',
  main: '#25D942',
  dark: '#0DA927',
  darker: '#003108',
  contrastText: '#FFFFFF',
};

const WARNING = {
  lighter: '#FFECD8',
  light: '#F1B171',
  main: '#EE8D2C',
  dark: '#B15F0C',
  darker: '#3D1F00',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FBD8D8',
  light: '#F65555',
  main: '#EE2C2C',
  dark: '#BD0F0F',
  darker: '#3A0000',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: { black: '#000000', white: '#FFFFFF' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export default function palette(themeMode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: { paper: '#FFFFFF', default: '#FFFFFF', neutral: GREY[200] },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  } as const;

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.16),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  } as const;

  return themeMode === 'light' ? light : dark;
}
